import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { ArrowUpRight, CheckSquare, Layers, Users, Sparkles } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F7F7F6] text-[#0D0D0D]">
      {/* Header */}
      <header className="border-b border-[#E2E2E2] bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <Logo size={64} textSize="text-xl" />
          <nav className="flex items-center gap-3">
            <a
              href="/catalog"
              data-testid="landing-nav-catalog"
              className="hidden sm:inline text-sm font-medium hover:underline underline-offset-4 text-[#5C5C5C] hover:text-[#0D0D0D]">
              Business Catalog
            </a>
            <a
              href="/shop"
              data-testid="landing-nav-shop"
              className="hidden sm:inline text-sm font-medium hover:underline underline-offset-4 text-[#5C5C5C] hover:text-[#0D0D0D]">
              Retail Shop
            </a>
            <span aria-hidden="true" className="hidden sm:inline text-neutral-300">·</span>
            <Link to="/login" data-testid="header-login-link" className="text-sm font-medium hover:underline underline-offset-4">Sign in</Link>
            <Link to="/register">
              <Button data-testid="header-get-started-btn" className="rounded-none bg-[#0D0D0D] hover:bg-[#FF3B30] text-white px-5 h-10 text-sm font-bold">
                Get started <ArrowUpRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-[#E2E2E2]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#5C5C5C] mb-8">
              <span className="inline-block w-2 h-2 bg-[#FF3B30] mr-2 -mb-px"></span>
              001 · Delegation, structured
            </div>
            <h1 className="font-heading font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
              Stop chasing tasks.<br/>
              <span className="text-[#FF3B30]">Start shipping.</span>
            </h1>
            <p className="mt-8 text-lg text-[#5C5C5C] max-w-xl leading-relaxed">
              Rim Task is the brutally simple delegation tool for teams who care about clarity. Assign, track,
              ship — without the dashboard fatigue.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/register">
                <Button data-testid="hero-cta-btn" className="rounded-none bg-[#FF3B30] hover:bg-[#0D0D0D] text-white h-12 px-6 text-sm font-bold tracking-wide">
                  Start free →
                </Button>
              </Link>
              <Link to="/login">
                <Button data-testid="hero-signin-btn" variant="outline" className="rounded-none border-[#0D0D0D] h-12 px-6 text-sm font-bold tracking-wide hover:bg-[#0D0D0D] hover:text-white">
                  I have an account
                </Button>
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 border border-[#0D0D0D] bg-white p-6 lg:p-8">
            <div className="font-mono text-xs uppercase tracking-[0.3em] mb-6 flex items-center justify-between">
              <span>Today / 17 tasks</span>
              <span className="text-[#FF3B30]">3 overdue</span>
            </div>
            <div className="space-y-3">
              {[
                { s: "in_progress", t: "Ship onboarding flow v2", p: "high" },
                { s: "todo", t: "Review marketing copy", p: "medium" },
                { s: "done", t: "Deploy auth refactor", p: "low" },
                { s: "blocked", t: "Resolve Stripe webhook", p: "urgent" },
              ].map((row) => (
                <div key={`${row.s}-${row.t}`} className="border border-[#E2E2E2] p-3 flex items-center gap-3 bg-white">
                  <span className={`w-2 h-2 status-${row.s}`}></span>
                  <span className="flex-1 text-sm font-medium truncate">{row.t}</span>
                  <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border priority-${row.p}`}>{row.p}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#5C5C5C] mb-4">002 · What you get</div>
        <h2 className="font-heading font-black tracking-tight text-4xl sm:text-5xl mb-12 max-w-2xl">
          A toolkit, not a swamp.
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 border border-[#E2E2E2] bg-white">
          {[
            { Icon: CheckSquare, t: "Kanban that snaps", d: "Drag tasks across status columns. Real-time updates. No fluff." },
            { Icon: Users, t: "Team workspaces", d: "Invite by email, assign by face, hold each other accountable." },
            { Icon: Layers, t: "Priorities & dates", d: "Urgent, high, medium, low. Due dates that actually surface." },
            { Icon: Sparkles, t: "AI co-pilot", d: "Auto-write briefs and break big tasks into shippable steps." },
          ].map(({ Icon, t, d }) => (
            <div key={t} className="p-8 border-r border-b border-[#E2E2E2] last:border-r-0">
              <Icon className="h-6 w-6 text-[#FF3B30]" />
              <h3 className="font-heading font-bold text-xl mt-6 mb-2">{t}</h3>
              <p className="text-sm text-[#5C5C5C] leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-[#E2E2E2] py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between text-xs font-mono uppercase tracking-[0.2em] text-[#5C5C5C]">
          <span>© 2026 Rim Task</span>
          <span>Built for teams who ship</span>
        </div>
      </footer>
    </div>
  );
}
